/**
 * Feature Gate
 * Utility for checking feature access based on user tier
 *
 * This module now uses tierInfo from entitlementService instead of
 * hardcoded tier name checks. The isPaid flag determines if a tier
 * is paid rather than checking specific tier names.
 */

import { entitlementService } from './entitlement-service.js';
import { authService } from '../auth/auth-service.js';

/**
 * Parse and validate a resolution string (e.g., "1920x1080")
 * @param {string} str - Resolution string in WIDTHxHEIGHT format
 * @returns {{width: number, height: number}} Parsed dimensions
 * @throws {Error} If format is invalid
 */
function parseResolution(str) {
  if (!str || typeof str !== 'string') {
    throw new Error(`Invalid resolution: expected string, got ${typeof str}`);
  }
  const match = str.match(/^(\d+)x(\d+)$/);
  if (!match) {
    throw new Error(`Invalid resolution format: "${str}" (expected WIDTHxHEIGHT)`);
  }
  return { width: Number(match[1]), height: Number(match[2]) };
}

/**
 * Normalize FPS value to a number
 * @param {string|number} value - FPS value (may be string from UI)
 * @returns {number} Normalized FPS
 * @throws {Error} If value cannot be parsed to valid FPS
 */
function normalizeFps(value) {
  const fps = typeof value === 'string' ? parseInt(value, 10) : value;
  if (!Number.isFinite(fps) || fps <= 0) {
    throw new Error(`Invalid FPS: ${value}`);
  }
  return fps;
}

class FeatureGate {
  constructor() {
    this.paywallCallback = null;
  }

  /**
   * Set callback for showing paywall modal
   */
  setPaywallCallback(callback) {
    this.paywallCallback = callback;
  }

  /**
   * Check if user can access a specific visualizer
   * Allows access if:
   * - '*' is in allowedVisualizers (all allowed)
   * - visualizerName is in allowedVisualizers
   * - It's a user visualizer (starts with 'user-') and canCreateCustomVisualizers is true
   */
  async canAccessVisualizer(visualizerName) {
    const allowed = await entitlementService.getAllowedVisualizers();

    // Wildcard allows all
    if (allowed.includes('*')) {
      return true;
    }

    // Explicit match
    if (allowed.includes(visualizerName)) {
      return true;
    }

    // User visualizers allowed if canCreateCustomVisualizers
    if (visualizerName.startsWith('user-')) {
      const entitlements = await entitlementService.getEntitlements();
      return entitlements.features?.canCreateCustomVisualizers === true;
    }

    return false;
  }

  /**
   * Check if user can add more songs to playlist
   */
  async canAddSongs(currentCount, addCount = 1) {
    const maxSongs = await entitlementService.getMaxPlaylistSongs();
    return (currentCount + addCount) <= maxSongs;
  }

  /**
   * Get how many more songs user can add
   */
  async getRemainingPlaylistSlots(currentCount) {
    const maxSongs = await entitlementService.getMaxPlaylistSongs();
    return Math.max(0, maxSongs - currentCount);
  }

  /**
   * Check if user can use a specific resolution
   * @param {string} resolution - Resolution string (e.g., "1920x1080")
   * @returns {Promise<boolean>} True if resolution is allowed for user's tier
   */
  async canUseResolution(resolution) {
    try {
      const maxRes = await entitlementService.getMaxResolution();
      const requested = parseResolution(resolution);
      const max = parseResolution(maxRes);
      return requested.width <= max.width;
    } catch (err) {
      console.error('[FeatureGate] canUseResolution error:', err.message);
      return false;
    }
  }

  /**
   * Check if user can use a specific FPS
   * @param {number|string} fps - FPS value (may be string from UI)
   * @returns {Promise<boolean>} True if FPS is allowed for user's tier
   */
  async canUseFps(fps) {
    try {
      const normalizedFps = normalizeFps(fps);
      const allowedFps = await entitlementService.getAllowedFps();
      return allowedFps.includes(normalizedFps);
    } catch (err) {
      console.error('[FeatureGate] canUseFps error:', err.message);
      return false;
    }
  }

  /**
   * Check if user can export (has remaining minutes or credits)
   */
  async canExport(durationSeconds) {
    const durationMinutes = durationSeconds / 60;
    const remaining = await entitlementService.getRemainingExportMinutes();

    if (remaining === Infinity) return true;
    if (durationMinutes <= remaining) return true;

    // Check if user has enough credits
    const credits = await entitlementService.getCreditBalance();
    const creditsNeeded = Math.ceil((durationMinutes - remaining) * 5);
    return credits >= creditsNeeded;
  }

  /**
   * Get current tier name
   */
  async getCurrentTier() {
    return await entitlementService.getTier();
  }

  /**
   * Check if user is on a paid tier
   * Uses tierInfo.isPaid from the database instead of hardcoded tier names
   */
  async isPaidTier() {
    return await entitlementService.isPaidTier();
  }

  /**
   * Get tier info object (displayName, badgeLabel, isPaid)
   */
  async getTierInfo() {
    return await entitlementService.getTierInfo();
  }

  /**
   * Check if user can create custom visualizers
   */
  async canCreateCustomVisualizers() {
    return await entitlementService.canCreateCustomVisualizers();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return authService.isAuthenticated();
  }

  /**
   * Check if a feature is available for the current tier
   * Generic feature check that shows paywall if not available
   */
  async checkFeature(featureName) {
    const entitlements = await entitlementService.getEntitlements();
    const isPaid = entitlements?.tierInfo?.isPaid || false;
    // SECURITY: freeMode must be verified (from server with valid signature)
    const isFreeMode = entitlements?.freeMode === true &&
                       await entitlementService.isFreeMode();
    const isAuthenticated = authService.isAuthenticated();
    const isAnonymous = authService.isAnonymous();

    // Feature requirements map
    const featureRequirements = {
      generate_playlists: {
        requiresPaid: true,
        title: 'Upgrade to Generate Playlists',
        message: 'AI playlist generation is available for Symphony tier and above.',
        requiredTier: 'symphony'
      },
      custom_visualizers: {
        requiresPaid: true,
        title: 'Upgrade for Custom Visualizers',
        message: 'Creating custom visualizers requires a paid subscription.',
        requiredTier: 'pro'
      }
    };

    const requirement = featureRequirements[featureName];
    if (!requirement) {
      // Unknown feature, allow by default
      return true;
    }

    // Free mode bypasses all tier restrictions
    if (isFreeMode) {
      return true;
    }

    // Check if user meets requirements
    if (requirement.requiresPaid && !isPaid) {
      this.showPaywall({
        feature: featureName,
        title: requirement.title,
        message: requirement.message,
        ctaText: !isAuthenticated || isAnonymous ? 'Sign Up Free' : 'Upgrade Plan'
      });
      return false;
    }

    return true;
  }

  /**
   * Show paywall for a blocked feature
   */
  showPaywall(options) {
    if (this.paywallCallback) {
      this.paywallCallback(options);
    } else {
      // Default alert if no callback set
      alert(options.message || 'This feature requires an upgrade.');
    }
  }

  /**
   * Gate a visualizer selection with paywall
   * Returns true if access granted, false if blocked
   */
  async gateVisualizerAccess(visualizerName, displayName) {
    const canAccess = await this.canAccessVisualizer(visualizerName);
    if (!canAccess) {
      const isPaid = await this.isPaidTier();
      this.showPaywall({
        feature: 'visualizer',
        visualizer: visualizerName,
        title: 'Upgrade Required',
        message: `The "${displayName}" visualizer requires a paid subscription.`,
        ctaText: isPaid ? 'Upgrade Plan' : 'View Plans'
      });
      return false;
    }
    return true;
  }

  /**
   * Gate playlist addition with paywall
   * Returns number of songs that can be added (may be less than requested)
   */
  async gatePlaylistAddition(currentCount, requestedCount) {
    const maxSongs = await entitlementService.getMaxPlaylistSongs();
    const canAdd = maxSongs - currentCount;

    if (canAdd <= 0) {
      const isAuthenticated = this.isAuthenticated();
      const isPaid = await this.isPaidTier();

      this.showPaywall({
        feature: 'playlist',
        title: !isAuthenticated ? 'Sign Up for More' : 'Upgrade Required',
        message: !isAuthenticated
          ? `Guest users can only add ${maxSongs} song. Sign up for free to add more songs!`
          : `Your plan allows up to ${maxSongs} songs in a playlist. Upgrade to add more.`,
        ctaText: !isAuthenticated ? 'Sign Up Free' : (isPaid ? 'Upgrade Plan' : 'View Plans')
      });
      return 0;
    }

    if (canAdd < requestedCount) {
      // Partial addition allowed
      this.showPaywall({
        feature: 'playlist',
        title: 'Playlist Limit Reached',
        message: `Only ${canAdd} of ${requestedCount} songs can be added. Your plan allows up to ${maxSongs} songs.`,
        ctaText: 'Upgrade for More',
        allowPartial: true
      });
    }

    return Math.min(canAdd, requestedCount);
  }

  /**
   * Gate resolution selection with paywall
   */
  async gateResolution(resolution) {
    const canUse = await this.canUseResolution(resolution);
    if (!canUse) {
      const maxRes = await entitlementService.getMaxResolution();
      const isPaid = await this.isPaidTier();
      this.showPaywall({
        feature: 'resolution',
        title: 'Upgrade Required',
        message: `${resolution} resolution requires a higher tier plan. Your current max is ${maxRes}.`,
        ctaText: isPaid ? 'Upgrade Plan' : 'View Plans'
      });
      return false;
    }
    return true;
  }

  /**
   * Gate FPS selection with paywall
   */
  async gateFps(fps) {
    const canUse = await this.canUseFps(fps);
    if (!canUse) {
      const isPaid = await this.isPaidTier();
      this.showPaywall({
        feature: 'fps',
        title: 'Upgrade Required',
        message: `${fps} fps export requires a paid subscription.`,
        ctaText: isPaid ? 'Upgrade Plan' : 'View Plans'
      });
      return false;
    }
    return true;
  }

  /**
   * Gate export with optional credit purchase prompt
   * Returns { allowed: boolean, useCredits?: boolean }
   */
  async gateExport(params) {
    const validation = await entitlementService.validateExport(params);

    if (validation.allowed) {
      return { allowed: true, exportToken: validation.exportToken };
    }

    const isPaid = await this.isPaidTier();
    const upgradeText = isPaid ? 'Upgrade Plan' : 'View Plans';

    // Handle different rejection reasons
    switch (validation.reason) {
      case 'server_unavailable':
        this.showPaywall({
          feature: 'connection',
          title: 'Connection Required',
          message: validation.message,
          ctaText: 'Retry'
        });
        return { allowed: false };

      case 'authentication_required':
        this.showPaywall({
          feature: 'auth',
          title: 'Sign In Required',
          message: validation.message,
          ctaText: 'Sign In'
        });
        return { allowed: false };

      case 'server_not_configured':
        this.showPaywall({
          feature: 'config',
          title: 'Setup Required',
          message: validation.message,
          ctaText: 'Learn More'
        });
        return { allowed: false };

      case 'visualizer_not_allowed':
        this.showPaywall({
          feature: 'visualizer',
          title: 'Upgrade Required',
          message: validation.message,
          ctaText: upgradeText
        });
        return { allowed: false };

      case 'resolution_exceeded':
        this.showPaywall({
          feature: 'resolution',
          title: 'Upgrade Required',
          message: validation.message,
          ctaText: upgradeText
        });
        return { allowed: false };

      case 'monthly_limit_exceeded':
        // Show credit purchase option if available
        if (validation.creditsAvailable >= validation.creditsRequired) {
          return new Promise((resolve) => {
            this.showPaywall({
              feature: 'export_limit',
              title: 'Monthly Limit Reached',
              message: validation.message,
              showCreditOption: true,
              creditsRequired: validation.creditsRequired,
              creditsAvailable: validation.creditsAvailable,
              onUseCredits: () => resolve({ allowed: true, useCredits: true }),
              onCancel: () => resolve({ allowed: false })
            });
          });
        } else {
          this.showPaywall({
            feature: 'export_limit',
            title: 'Monthly Limit Reached',
            message: validation.message,
            showBuyCredits: true,
            creditsRequired: validation.creditsRequired,
            creditsAvailable: validation.creditsAvailable,
            ctaText: 'Buy Credits'
          });
          return { allowed: false };
        }

      default:
        this.showPaywall({
          feature: 'unknown',
          title: 'Export Blocked',
          message: validation.message || 'Unable to export at this time.',
          ctaText: 'Learn More'
        });
        return { allowed: false };
    }
  }

  /**
   * Filter resolution options based on tier
   * @param {Array<{value: string, label: string}>} options - Resolution options
   * @returns {Promise<Array>} Options with locked status added
   */
  async filterResolutionOptions(options) {
    const maxRes = await entitlementService.getMaxResolution();
    let maxWidth;
    try {
      maxWidth = parseResolution(maxRes).width;
    } catch (err) {
      console.error('[FeatureGate] filterResolutionOptions: invalid maxRes:', err.message);
      maxWidth = 0; // Lock all options on error
    }

    return options.map(opt => {
      let isLocked = true; // Default to locked on parse error
      try {
        const width = parseResolution(opt.value).width;
        isLocked = width > maxWidth;
      } catch (err) {
        console.warn('[FeatureGate] filterResolutionOptions: invalid option:', opt.value);
      }
      return {
        ...opt,
        disabled: isLocked,
        locked: isLocked,
        label: isLocked ? `${opt.label} 🔒` : opt.label
      };
    });
  }

  /**
   * Filter FPS options based on tier
   * @param {Array<{value: string, label: string}>} options - FPS options
   * @returns {Promise<Array>} Options with locked status added
   */
  async filterFpsOptions(options) {
    const allowedFps = await entitlementService.getAllowedFps();

    return options.map(opt => {
      let isLocked = true; // Default to locked on parse error
      try {
        const fps = normalizeFps(opt.value);
        isLocked = !allowedFps.includes(fps);
      } catch (err) {
        console.warn('[FeatureGate] filterFpsOptions: invalid option:', opt.value);
      }
      return {
        ...opt,
        disabled: isLocked,
        locked: isLocked,
        label: isLocked ? `${opt.label} 🔒` : opt.label
      };
    });
  }

  /**
   * Get visualizer list with tier indicators
   */
  async getVisualizersWithTiers(visualizers) {
    const allowed = await entitlementService.getAllowedVisualizers();

    // Wildcard means all visualizers are unlocked
    if (allowed.includes('*')) {
      return visualizers.map(viz => ({ ...viz, locked: false }));
    }

    return visualizers.map(viz => ({
      ...viz,
      locked: !allowed.includes(viz.name),
      displayName: !allowed.includes(viz.name) ? `${viz.displayName} 🔒` : viz.displayName
    }));
  }
}

// Export singleton instance
export const featureGate = new FeatureGate();
